/* eslint-disable import/no-unresolved */
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { PlanUpgradeFlow } from 'src/flows/planUpgrade';
import { useStore } from '../../../../store';
import { FirstImpressionPage } from '../../../../components/FirstImpressionPage/FirstImpressionPage';
import { VIEWS_MAP } from '../../../../router/constants';
import {
  FirstImpressionReportStartAnimation,
  FirstImpressionReportClickCtaAfterAnimation,
} from '../../../../services/firstImpressionAnalytics.service';
import { ISummaryData } from '../../../../types';
import { canUpgradeToUseFeature, validateFeatureFlag } from '../../../../services/featureFlagService';
import { FEATURE_FLAG } from '../../../../constants/general';
import { featureAccess, LimitedFeatureType } from '../../../../services/appAccess.service';
import { FeatureAccess } from '../../../AccountSettings/components/rolesAndPermissions/PermissionsList';

export const BillsFirstImpression: React.FC = () => {
  const [key, setKey] = useState(0); // State to force re-render
  const navigate = useNavigate();
  const [shouldShowUpgrade] = useState<boolean>(
    canUpgradeToUseFeature(FEATURE_FLAG.BILL_PAY) && !validateFeatureFlag(FEATURE_FLAG.BILL_PAY),
  );
  const [isPlanUpgradeFlowOpen, setIsPlanUpgradeFlowOpen] = useState(false);
  const billPayPermission = featureAccess(FEATURE_FLAG.BILL_PAY, LimitedFeatureType.BillPay);
  const planUpgradePermission = featureAccess(FEATURE_FLAG.PLAN_UPGRADE, LimitedFeatureType.UpgradePlan);
  const isButtonDisabled = shouldShowUpgrade
    ? planUpgradePermission !== FeatureAccess.READ_WRITE
    : billPayPermission !== FeatureAccess.READ_WRITE;
  const { t } = useTranslation('firstImperssionEN');
  const [finishedState, setFinishedState] = useState(false);
  const summaryData: ISummaryData = useStore((state) => state.summary);
  const planType = summaryData.customer.customerProfile;
  useEffect(() => {
    FirstImpressionReportStartAnimation({ customerProfilePLan: planType, value: 'bills' });
  }, []);
  const handleLottieClick = () => {
    if (!finishedState) return;
    setKey((prevKey) => prevKey + 1); // Update the key to force re-render
    setFinishedState(false);
  };

  const eventListeners = [
    {
      event: 'complete',
      callback: () => {
        setFinishedState(true);
      },
    },
  ];
  const title1 = t('bills.title1');
  const title2 = t('bills.title2');
  const buttonText = !shouldShowUpgrade ? t('bills.button') : t('bills.upgradeButton');
  const handleSubmit = () => {
    FirstImpressionReportClickCtaAfterAnimation({ customerProfilePLan: planType, value: 'bills' });
    if (!shouldShowUpgrade) navigate(`/${VIEWS_MAP.BILLS.BASE}/${VIEWS_MAP.BILLS.ADD_NEW_BILL}`);
    setIsPlanUpgradeFlowOpen(true);
  };
  return (
    <div className=' flex flex-col fadeIn justify-center'>
      <FirstImpressionPage>
        <FirstImpressionPage.Header
          texts={[title1, title2]}
          isLoadingState={false}
          onSubmit={handleSubmit}
          buttonText={buttonText}
          buttonId='addNewBillBtn'
          buttonDisabled={isButtonDisabled}
        />
        <FirstImpressionPage.LottieSection
          key={key} // Use the key to force re-render
          eventListeners={eventListeners}
          lottie='https://lili-mobile-images.s3.us-east-1.amazonaws.com/webapp/lottie/Bill+Pay_13.json'
          isLoop={false}
          onLottieClick={handleLottieClick}
          finishedState={finishedState}
        />
      </FirstImpressionPage>
      {isPlanUpgradeFlowOpen && (
        <PlanUpgradeFlow
          onCloseClick={() => {
            setIsPlanUpgradeFlowOpen(false);
          }}
          origin='categorization'
        />
      )}
    </div>
  );
};
